/**
 * Exposure Modal component module.
 * @module advisor/components/modals/PerformanceSecuritiesModal
 *
 */
import React from 'react';
import {connect} from 'react-redux';
import BaseModal from '../types/Base';
import {_removeModal} from '../../../store';
import PropTypes from 'prop-types';
import debug from 'debug';
import {Column, LoadingSpinner, Tabs} from '../../../index';
import useSWR from 'swr';
import Bar from '../../graphs/Bar';
import {getNegativeColor, getPositiveColor, COLOR_ZERO_VALUE} from '../../../utils/colors';
import SectionTitle from '../../SectionTitle';

const adjustLimit = (num, min) => {
  if (num > 0) {
    return (min ? num * 0.8 : num * 1.2) * 100;
  }
  if (num < 0) {
    return (min ? num * 1.2 : num * 0.8) * 100;
  }
  return num * 100;
};

const log = debug('circleblack:modals:PerformanceSecuritiesModal');

/**
 * PerformanceSecuritiesModal
 * @name PerformanceSecuritiesModal
 */
const PerformanceSecuritiesModal = props => {
  const {content, removeModal} = props;
  const {containerID, period} = content;
  const {data: securities} = useSWR(`/containers/${containerID}/securities/performance?period=${period.id}&top=5&bottom=5`);

  const buildPerformer = security => ({
    name: security.name,
    color: (security.performance < 0 && getNegativeColor())
      || (security.performance > 0 && getPositiveColor())
      || COLOR_ZERO_VALUE,
    y: 1 * security.performance * 100,
  });

  const buildContributor = security => ({
    name: security.name,
    color: (security.adjustedContribution < 0 && getNegativeColor())
      || (security.adjustedContribution > 0 && getPositiveColor())
      || COLOR_ZERO_VALUE,
    y: 1 * security.adjustedContribution * 100,
  });

  log('rendering');
  const tabs = React.useMemo(() => securities && [
    {
      title: 'Performers',
      content: <Column>
        <SectionTitle title={'Top Performers'}/>
        <Bar min={adjustLimit(Math.min(...securities.performances.map(d => d.performance)), true)}
             max={adjustLimit(Math.max(...securities.performances.map(d => d.performance)), false)}
             data={securities.performances.slice(0, (securities.performances.length / 2) + (securities.performances.length % 2))
               .map(buildPerformer)}/>
        <SectionTitle title={'Bottom Performers'}/>
        <Bar min={adjustLimit(Math.min(...securities.performances.map(d => d.performance)), true)}
             max={adjustLimit(Math.max(...securities.performances.map(d => d.performance)), false)}
             data={securities.performances.slice((securities.performances.length / 2) + (securities.performances.length % 2))
               .map(buildPerformer)}/>
      </Column>,
    },
    {
      title: 'Contributors',
      content: <Column>
        <SectionTitle title={'Top Contributors'}/>
        <Bar min={adjustLimit(Math.min(...securities.contributions.map(d => d.adjustedContribution)), true)}
             max={adjustLimit(Math.max(...securities.contributions.map(d => d.adjustedContribution)), false)}
             data={securities.contributions.slice(0, (securities.contributions.length / 2) + (securities.contributions.length % 2))
               .map(buildContributor)}/>
        <SectionTitle title={'Bottom Contributors'}/>
        <Bar min={adjustLimit(Math.min(...securities.contributions.map(d => d.adjustedContribution)), true)}
             max={adjustLimit(Math.max(...securities.contributions.map(d => d.adjustedContribution)), false)}
             data={securities.contributions.slice((securities.contributions.length / 2) + (securities.contributions.length % 2))
               .map(buildContributor)}/>
      </Column>,
    },
  ], [securities]);
  return <BaseModal onClose={() => removeModal(content)} title={`${period.name} Performance Analysis`} size={'lg'}>
    {securities
      ? <Tabs defaultSelection={0} displayTabTitles={true} minTabWidth={'100px'} tabWidth={'150px'} tabs={tabs}/>
      : <LoadingSpinner/>}
  </BaseModal>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  removeModal: modalToRemove => dispatch(_removeModal(modalToRemove)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PerformanceSecuritiesModal);

PerformanceSecuritiesModal.propTypes = {
  removeModal: PropTypes.func.isRequired,
  content: PropTypes.shape({
    containerID: PropTypes.number.isRequired,
    period: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }),
};
